import { cn } from "@/lib/utils";
import AvatarStack from "@/components/AvatarStack";
import TaskRowMenu from "@/pages/Tasks/components/TaskRowMenu";
import { titleFor, type Task, type WhyTone, dueWords, whyFor, workingDue } from "@/pages/Tasks/task";

const TONE_CLASS = {
  overdue: "text-destructive font-semibold",
  today: "text-warning font-semibold",
  none: "text-muted-foreground",
} as const;

// Second-line tones. `info` mirrors the drawer's "Waiting for … to review"
// line so the row reads the same at a glance.
const WHY_CLASS: Record<WhyTone, string> = {
  neutral: "text-muted-foreground",
  info: "font-medium text-info",
  amber: "font-medium text-warning",
};

// One task row: title, a second line only when it changes a decision, due
// words, one verb button, ⋯ on hover. Row click (and Enter/Space) opens the
// details drawer; the verb button is the doorway to the real work page.
export default function TaskRow({ task, showPlanName, selected, onOpenDetails, onPause }: {
  task: Task;
  /** Disambiguation line when the same title appears twice — never alongside a why-phrase. */
  showPlanName: boolean;
  /** This row is the one the details panel is showing. */
  selected?: boolean;
  onOpenDetails: () => void;
  onPause: () => void;
}) {
  const due = dueWords(workingDue(task));
  const why = whyFor(task);
  const label = task.market_name ?? task.org_node_name;
  const sub = why ?? (showPlanName && label ? { text: label, tone: "neutral" as const } : null);

  return (
    <div
      data-task-row
      role="button"
      tabIndex={0}
      onClick={onOpenDetails}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenDetails();
        }
      }}
      className={cn(
        "group relative flex min-h-[52px] cursor-pointer items-center gap-5 px-5 py-3 transition-colors",
        "first:rounded-t-lg last:rounded-b-lg",
        "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
        selected && "task-notch"
      )}
    >
      {/* Selection marker: solid bar on the row's left edge. */}
      {selected && <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-primary" />}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold tracking-tight text-foreground">{titleFor(task)}</p>
        {sub && (
          <p className={cn("truncate text-xs", WHY_CLASS[sub.tone])}>
            {sub.text}
          </p>
        )}
      </div>

      <AvatarStack team={task.responsible} claimedBy={task.claimed_by} />

      {/* Fixed width, not min-width: every row's date column must be the same
          size or the badge/avatar column drifts between rows. Sized to the
          longest sub-line ("(in 11 months)"). */}
      <span className={cn("w-[110px] shrink-0 text-right text-xs tabular-nums", TONE_CLASS[due.tone])}>
        {due.text}
        {due.sub && <span className="block text-[11px]">{due.sub}</span>}
      </span>

      <TaskRowMenu task={task} onPause={onPause} />


    </div>
  );
}
